/**
 * 语言状态管理 - Zustand
 * 支持本地存储和服务端同步
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ActiveLanguage } from '../i18n/types';
import { isLanguageActive, isRTL } from '../i18n/types';
import { detectBrowserLanguage } from '../i18n/detectLanguage';

interface LanguageState {
  // 当前语言
  language: ActiveLanguage;
  // 是否正在同步到服务端
  isSyncing: boolean;
  // 最后同步时间
  lastSyncedAt: number | null;
  
  // Actions
  setLanguage: (lang: ActiveLanguage) => void;
  initializeLanguage: () => void;
  syncToServer: () => Promise<void>;
  syncFromServer: () => Promise<void>;
}

export const useLanguageStore = create<LanguageState>()(
  persist(
    (set, get) => ({
      language: 'en', // 默认英文，在 initialize 时会被覆盖
      isSyncing: false,
      lastSyncedAt: null,

      /**
       * 初始化语言设置
       * 优先级：URL > localStorage > 浏览器检测 > 默认
       */
      initializeLanguage: () => {
        // 1. 检查 URL 路径（最高优先级）
        if (typeof window !== 'undefined') {
          const pathLang = window.location.pathname.match(/^\/(en|zh-CN|ja)(?:\/|$)/);
          if (pathLang) {
            const lang = pathLang[1] as ActiveLanguage;
            set({ language: lang });
            updateDocumentAttributes(lang);
            return;
          }
        }

        // 2. Zustand persist 已自动从 localStorage 恢复状态。
        //    如果 persist key 存在，说明用户之前选择过语言，直接用恢复的值。
        if (typeof window !== 'undefined') {
          const persistedData = localStorage.getItem('language-storage');
          if (persistedData) {
            const currentLang = get().language;
            updateDocumentAttributes(currentLang);
            return;
          }
        }

        // 3. 首次访问 — 浏览器检测
        const browserLang = detectBrowserLanguage();
        set({ language: browserLang });
        updateDocumentAttributes(browserLang);
      },

      /**
       * 设置语言（本地 + 尝试同步到服务端）
       */
      setLanguage: async (lang: ActiveLanguage) => {
        const prevLang = get().language;
        if (prevLang === lang) return;

        // 更新本地状态
        set({ language: lang });
        
        // 更新文档属性
        updateDocumentAttributes(lang);
        
        // 同步到服务端
        await get().syncToServer();
        
        // 触发语言切换事件（供其他组件监听）
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('languagechange', { 
            detail: { from: prevLang, to: lang } 
          }));
        }
      },

      /**
       * 同步语言设置到服务端
       */
      syncToServer: async () => {
        const { language, isSyncing } = get();
        
        if (isSyncing) return;
        if (typeof window === 'undefined') return;

        set({ isSyncing: true });

        try {
          // Cookie session: server returns 401 if not logged in — treat as no-op.
          const response = await fetch('/api/users/preferences/language', {
            method: 'PUT',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ language }),
          });

          if (response.ok) {
            set({ lastSyncedAt: Date.now() });
          }
        } catch (error) {
          console.error('Failed to sync language preference:', error);
        } finally {
          set({ isSyncing: false });
        }
      },

      /**
       * 从服务端同步语言设置
       * 用于登录后恢复用户的语言偏好
       */
      syncFromServer: async () => {
        if (typeof window === 'undefined') return;

        set({ isSyncing: true });

        try {
          const response = await fetch('/api/users/preferences/language', {
            credentials: 'include',
          });

          if (response.ok) {
            const data = await response.json();
            if (data.language && isLanguageActive(data.language)) {
              const serverLang = data.language as ActiveLanguage;
              const currentLang = get().language;
              
              if (serverLang !== currentLang) {
                set({ language: serverLang });
                updateDocumentAttributes(serverLang);
                
                // 触发事件
                window.dispatchEvent(new CustomEvent('languagechange', { 
                  detail: { from: currentLang, to: serverLang, source: 'server' } 
                }));
              }
              
              set({ lastSyncedAt: Date.now() });
            }
          }
        } catch (error) {
          console.error('Failed to fetch language preference:', error);
        } finally {
          set({ isSyncing: false });
        }
      },
    }),
    {
      name: 'language-storage',
      partialize: (state) => ({ 
        language: state.language,
        lastSyncedAt: state.lastSyncedAt,
      }),
    }
  )
);

/**
 * 更新 HTML 文档的 lang 和 dir 属性
 */
function updateDocumentAttributes(lang: ActiveLanguage) {
  if (typeof document === 'undefined') return;
  
  const html = document.documentElement;
  html.setAttribute('lang', lang);
  html.setAttribute('dir', isRTL(lang) ? 'rtl' : 'ltr');
}

/**
 * 监听登录事件，登录后从服务端同步语言
 */
if (typeof window !== 'undefined') {
  window.addEventListener('user-login', () => {
    const { syncFromServer } = useLanguageStore.getState();
    syncFromServer();
  });
}
